import React, { useState } from "react";
import { Mail, Phone, MessageCircle, Send, Instagram, Facebook } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import useTitle from "../lib/useTitle";
import { CONTACT } from "../lib/constants";

const initial = { full_name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const { t } = useI18n();
  useTitle(t.nav.contact);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) return toast.error("Required fields");
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent ✉");
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  const inputCls = "w-full bg-sand-100 border border-sand-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-wostel-cyan focus:ring-2 focus:ring-wostel-cyan/20 transition";

  return (
    <main className="container-x py-16" data-testid="contact-page">
      <Toaster position="top-center" richColors />
      <div className="grid md:grid-cols-12 gap-16">
        <div className="md:col-span-5">
          <span className="eyebrow">{t.nav.contact}</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">{t.contact.title}</h1>
          <p className="mt-5 text-lg text-wostel-ink/70 leading-relaxed">
            Djerba · Tunisia
          </p>

          <ul className="mt-10 space-y-5">
            <li><a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 hover:text-wostel-cyan" data-testid="contact-email"><Mail size={18}/> {CONTACT.email}</a></li>
            <li><a href={`tel:${CONTACT.phone.replace(/\s/g,'')}`} className="flex items-center gap-3 hover:text-wostel-cyan" data-testid="contact-phone"><Phone size={18}/> {CONTACT.phone}</a></li>
            <li><a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-wostel-cyan" data-testid="contact-wa"><MessageCircle size={18}/> {CONTACT.whatsappPretty}</a></li>
            <li className="flex gap-4 pt-2">
              <a href={CONTACT.instagram} target="_blank" rel="noreferrer" className="hover:text-wostel-cyan" data-testid="contact-ig"><Instagram size={20}/></a>
              <a href={CONTACT.facebook} target="_blank" rel="noreferrer" className="hover:text-wostel-cyan" data-testid="contact-fb"><Facebook size={20}/></a>
            </li>
          </ul>
        </div>

        <form onSubmit={submit} className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="contact-form">
          <input required placeholder={t.creator.form.full_name + "*"} value={form.full_name} onChange={onChange("full_name")} className={inputCls + " sm:col-span-2"} data-testid="contact-input-name" />
          <input required type="email" placeholder={t.creator.form.email + "*"} value={form.email} onChange={onChange("email")} className={inputCls} data-testid="contact-input-email" />
          <input placeholder={t.contact.phone} value={form.phone} onChange={onChange("phone")} className={inputCls} data-testid="contact-input-phone" />
          <input placeholder="Subject" value={form.subject} onChange={onChange("subject")} className={inputCls + " sm:col-span-2"} data-testid="contact-input-subject" />
          <textarea required rows={6} placeholder="Message*" value={form.message} onChange={onChange("message")} className={inputCls + " sm:col-span-2"} data-testid="contact-input-message" />
          <button type="submit" disabled={submitting} className="btn-pill bg-wostel-ink text-sand-50 hover:bg-wostel-cyan disabled:opacity-50 sm:col-span-2 justify-self-start" data-testid="contact-submit">
            <Send size={16}/> {submitting ? "..." : "Send"}
          </button>
        </form>
      </div>
    </main>
  );
}
